import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Modal, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import HelperMethods from '../../services/exports/HelperMethods';
import LicenseActivatedResult from './LicenseActivatedResult';
import PaywallPlanCard from './paywall/PaywallPlanCard';
import PaywallDueToday from './paywall/PaywallDueToday';
import PaywallPaymentMethod from './paywall/PaywallPaymentMethod';
import { findLicenseProduct, licenseErrorMessage, trialDaysOwed } from '../../services/tenant/providerBilling';

/**
 * The subscribe modal (PROD-494), shared by every paywall trigger.
 *
 * The purchase is three steps (WLP paywall redesign): this modal's offer with
 * "Continue to payment", Stripe Checkout in setup mode (Stripe's own page), and
 * this modal again with the card on file, the terms and the button that
 * starts the trial. A partner who already has a card skips straight to the
 * last step. Nothing warns or asks for terms before the partner has chosen to
 * go on.
 *
 * Around those, the same component also covers a plain confirmation when the
 * restaurant already holds the product (Go Live always opens the modal, and a
 * partner past its trial must not be shown a price for something it already
 * pays for), the success state after buying, and a waiting/failed body while
 * the entitlements are not there yet — the trigger has been pressed, so the
 * modal must never be invisible.
 *
 * Every date in the copy comes from the entitlements payload. The backend
 * decides whether a free period is owed (once per restaurant, ever) and which
 * 1st the first invoice lands on; deriving either here would let the offer
 * differ from the bill.
 *
 * `onSubscribe` is injected: Go Live buys the licence and launches in ONE
 * request, while the other triggers activate on their own. It resolves to
 * `{ ok }`, `{ ok: false, error }`, or `{ ok: true, dismissed: true }` when the
 * caller has closed the modal itself and no success state should show.
 *
 * `requiresPaymentMethod` means the backend would refuse the purchase (or the
 * launch) for want of a card, so the offer ends in "Continue to payment".
 * `onAddPaymentMethod` leaves for Stripe Checkout and resolves only on failure —
 * success is a full-page redirect; "Change" on the card row takes the same
 * trip, since a new card replaces the old one. `paymentMethodPending` means the
 * partner is back from Stripe but the card has not arrived yet, so the last
 * step shows it as confirming and offers `onCheckPaymentMethod`.
 *
 * `domain` names where a website product will go live, for the subtitle.
 *
 * `extraContent` is what a trigger needs from the partner on top of the
 * purchase, shown on the last step (the Stripe trip is a full-page redirect,
 * so anything asked before it would be lost). `onBeforeSubmit` runs before
 * anything is bought and returns false to stop there, so the trigger can show
 * its own inline error rather than buy a licence for an action that would
 * then be refused.
 */
export default function LicensePaywallModal({
  open = false,
  productKey,
  entitlements,
  company = null,
  domain = null,
  error = null,
  successTitle = null,
  onRetry = () => {},
  onBack = () => {},
  onDone = () => {},
  onSubscribe = async () => ({ ok: true }),
  requiresPaymentMethod = false,
  paymentMethodPending = false,
  onAddPaymentMethod = async () => ({ ok: true }),
  onCheckPaymentMethod = async () => null,
  extraContent = null,
  onBeforeSubmit = () => true,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:ProviderBilling:LicensePaywallModal' });

  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [cardError, setCardError] = useState(false);
  const [checkingCard, setCheckingCard] = useState(false);
  const [cardStillMissing, setCardStillMissing] = useState(false);
  const mounted = useRef(true);

  const product = useMemo(() => findLicenseProduct(entitlements, productKey), [entitlements, productKey]);
  // A licence bought in this sitting whose follow-up action failed leaves the
  // entitlements saying "licensed" while the partner is still mid-purchase:
  // the modal stays the purchase it is, so the retry that completes it is
  // celebrated rather than waved through as a confirmation.
  const licensed = Boolean(product?.licensed) && !purchased;
  // The offer's own step: no card yet, so it ends in Stripe Checkout. Straight
  // back from Stripe the card is on its way, and the last step says so instead.
  const reviewing = requiresPaymentMethod && !paymentMethodPending;

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setAccepted(false);
      setSubmitError(null);
      setSubmitting(false);
      setSucceeded(false);
      setPurchased(false);
      setAddingCard(false);
      setCardError(false);
      setCheckingCard(false);
      setCardStillMissing(false);
    }
  }, [open, productKey]);

  async function addCard() {
    setAddingCard(true);
    setCardError(false);
    setCardStillMissing(false);

    const result = await onAddPaymentMethod();

    if (!mounted.current || result?.ok !== false) {
      return;
    }

    setAddingCard(false);
    setCardError(true);
  }

  async function checkCard() {
    setCheckingCard(true);
    setCardStillMissing(false);

    const standing = await onCheckPaymentMethod();

    if (!mounted.current) {
      return;
    }

    setCheckingCard(false);
    // Once it has landed the row turns into the card on its own; only a miss needs saying.
    setCardStillMissing(!standing?.payment_method_on_file);
  }

  async function submit() {
    if (onBeforeSubmit() === false) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const result = await onSubscribe(productKey);

    // The wizard's footer sits outside the modal: a partner can leave the
    // screen mid-purchase, and a screen they have left must not be written to.
    if (!mounted.current) {
      return;
    }

    setSubmitting(false);

    if (result?.purchased) {
      setPurchased(true);
    }

    if (result?.ok === false) {
      setSubmitError(result.error ?? true);

      return;
    }

    if (result?.dismissed) {
      return;
    }

    // A confirmation has nothing to celebrate; a purchase does.
    if (licensed) {
      onDone();

      return;
    }

    setSucceeded(true);
  }

  // Plain dates in the partner's billing calendar, never the instant: an
  // instant formatted in the restaurant's timezone can name a day the ledger
  // never bills from.
  const formatDay = (day) => (day ? moment(day).format('LL') : null);
  const price = product ? HelperMethods.formatCurrency(product.amount, product.currency) : null;
  const trialDays = licensed ? 0 : trialDaysOwed(product);
  const freeUntil = formatDay(product?.trial_ends_at_if_activated);
  const billingStart = formatDay(product?.first_billing_date);

  function subtitle() {
    if (domain && t(`products.${productKey}.subtitle_domain`, { defaultValue: '' })) {
      return t(`products.${productKey}.subtitle_domain`, { name: company?.name, domain });
    }

    return company?.name ? t('subtitle', { name: company.name }) : null;
  }

  function title() {
    if (licensed) {
      return t('title_confirm');
    }

    return t(`products.${productKey}.title`, { defaultValue: t('title') });
  }

  function renderHeader() {
    const closeButton = (
      <Button
        type="text"
        className="-mt-2xs shrink-0 text-brand-inkGrey-grey_3"
        style={{ width: 44, height: 44 }}
        aria-label={t('buttons.close')}
        data-testid="license-paywall-close"
        icon={<CloseOutlined className="text-[18px]" />}
        onClick={succeeded ? onDone : onBack}
      />
    );

    // The success result carries its own heading.
    if (succeeded) {
      return <div className="flex justify-end px-md pt-md">{closeButton}</div>;
    }

    const sub = product ? subtitle() : null;

    return (
      <div className="flex items-start justify-between gap-md pl-[28px] pr-lg pt-lg">
        <div className="flex flex-col gap-2xs">
          {reviewing && product && (
            <span
              className="text-[13px] font-semibold uppercase tracking-[0.02em] text-brand-inkGrey-grey_3"
              data-testid="license-paywall-step"
            >
              {t('step', { current: 1, total: 2 })}
            </span>
          )}
          <h2 className="m-none text-[22px] font-bold leading-tight tracking-[-0.01em] text-brand-text-default">
            {title()}
          </h2>
          {sub && (
            <span className="text-sm leading-normal text-brand-inkGrey-grey_3" data-testid="license-paywall-subtitle">
              {sub}
            </span>
          )}
        </div>
        {closeButton}
      </div>
    );
  }

  function renderPending() {
    if (error && !entitlements) {
      return (
        <Alert
          type="error"
          showIcon
          data-testid="license-paywall-load-error"
          title={licenseErrorMessage(error) ?? t('load_error')}
          action={
            <Button size="small" onClick={onRetry}>
              {t('buttons.retry')}
            </Button>
          }
        />
      );
    }

    // Loaded, but this product is not in the catalogue for this restaurant
    // (unpriced products are filtered out by the backend).
    if (entitlements) {
      return <Alert type="warning" showIcon data-testid="license-paywall-unavailable" title={t('unavailable')} />;
    }

    return (
      <div className="flex justify-center py-lg" data-testid="license-paywall-loading">
        <Spin tip={t('loading')} />
      </div>
    );
  }

  function activeNote() {
    if (!licensed) {
      return null;
    }

    const trialEnd = formatDay(product.trial_ends_on);

    return trialEnd
      ? t('active_with_trial', { plan: product.name, date: trialEnd })
      : t('active', { plan: product.name });
  }

  function renderPaymentMethod() {
    // Straight back from Stripe: the card is on its way.
    if (paymentMethodPending) {
      return (
        <PaywallPaymentMethod
          pending
          checking={checkingCard}
          stillMissing={cardStillMissing}
          changing={addingCard}
          onCheck={checkCard}
          onChange={addCard}
        />
      );
    }

    // No row to show for a login that is let through without one (a super
    // admin acting for the partner cannot enter the partner's card).
    if (!entitlements?.payment_method_on_file) {
      return null;
    }

    return (
      <PaywallPaymentMethod paymentMethod={entitlements.payment_method} changing={addingCard} onChange={addCard} />
    );
  }

  function renderOffer() {
    return (
      <div className="flex flex-col gap-[20px] px-[28px] pt-lg">
        <PaywallPlanCard
          product={product}
          productKey={productKey}
          price={price}
          trialDays={trialDays}
          activeNote={activeNote()}
          pricingTier={product.pricing_tier ?? null}
        />

        {!reviewing && renderPaymentMethod()}

        {!licensed && (
          <PaywallDueToday
            dueToday={HelperMethods.formatCurrency(0, product.currency)}
            billingStart={billingStart}
            price={price}
          />
        )}

        {!reviewing && extraContent && (
          <form
            data-testid="license-paywall-extra"
            onSubmit={(event) => {
              // Enter in the trigger's field is the button below, with its gating.
              event.preventDefault();

              if (!requiresPaymentMethod && (licensed || accepted) && !submitting) {
                submit();
              }
            }}
          >
            {extraContent}
          </form>
        )}

        {!reviewing && !licensed && (
          <Checkbox
            checked={accepted}
            data-testid="license-paywall-terms"
            className="items-start text-sm leading-normal"
            onChange={(event) => setAccepted(event.target.checked)}
          >
            <Trans
              t={t}
              values={{ terms_url: entitlements?.terms_url }}
              components={{ a: <a className="font-semibold text-brand-text-default underline" /> }}
            >
              terms
            </Trans>
          </Checkbox>
        )}

        {cardError && (
          <Alert type="error" showIcon title={t('payment_method.error')} data-testid="license-paywall-add-card-error" />
        )}

        {submitError && (
          <Alert
            type="error"
            showIcon
            title={licenseErrorMessage(submitError) ?? t('error')}
            data-testid="license-paywall-error"
          />
        )}
      </div>
    );
  }

  function renderFooter() {
    const cta = {
      block: true,
      size: 'large',
      color: 'default',
      variant: 'solid',
      className: 'h-[52px] text-base font-semibold',
    };

    if (reviewing) {
      return (
        <>
          <Button {...cta} loading={addingCard} onClick={addCard} data-testid="license-paywall-add-card">
            {t('buttons.continue_to_payment')}
          </Button>
          <span className="text-center text-[13px] text-brand-inkGrey-grey_3">{t('no_charge_today')}</span>
        </>
      );
    }

    if (licensed) {
      return (
        <Button
          {...cta}
          disabled={requiresPaymentMethod}
          loading={submitting}
          onClick={submit}
          data-testid="license-paywall-submit"
        >
          {t('buttons.continue')}
        </Button>
      );
    }

    return (
      <>
        <Button
          {...cta}
          disabled={requiresPaymentMethod || !accepted}
          loading={submitting}
          onClick={submit}
          data-testid="license-paywall-submit"
        >
          {trialDays > 0 ? t('buttons.start_trial', { days: trialDays }) : t('buttons.subscribe')}
        </Button>
        <span className="text-center text-[13px] text-brand-inkGrey-grey_3" data-testid="license-paywall-footnote">
          {trialDays > 0 && freeUntil ? t('no_charge_until', { date: freeUntil }) : t('no_charge_today')}
        </span>
      </>
    );
  }

  function renderBody() {
    if (succeeded) {
      return (
        <div className="px-lg pb-lg">
          <LicenseActivatedResult
            company={company}
            entitlements={entitlements}
            productKey={productKey}
            title={successTitle}
            onContinue={onDone}
          />
        </div>
      );
    }

    if (!product) {
      return <div className="px-[28px] pb-[28px] pt-lg">{renderPending()}</div>;
    }

    return (
      <>
        {renderOffer()}
        <div className="flex flex-col gap-sm px-[28px] pb-[28px] pt-lg">{renderFooter()}</div>
      </>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={succeeded ? onDone : onBack}
      closable={false}
      footer={null}
      destroyOnHidden
      width={520}
      styles={{ container: { padding: 0, borderRadius: 16, overflow: 'hidden' } }}
      data-testid="license-paywall"
    >
      {renderHeader()}
      {renderBody()}
    </Modal>
  );
}
